import { MouseEvent, ReactNode } from "react";
import { Row, Col, ButtonProps } from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import {
  Body,
  DialogAppearance,
  IceCreamBody,
  IceCreamButton,
  IceCreamIcon,
  IceCreamTitle,
  Modal,
  Title,
} from "./ConfirmDialog.style";

const styles = {
  buttonGroup: { marginTop: "16px" },
  button: { margin: "4px 0px" },
};

export type ConfirmDialogProps = {
  title: ReactNode;
  description: ReactNode;
  onConfirm?: () => void;
  onReject?: () => void;
  isVisible?: boolean;
  confirmButtonProps?: ButtonProps;
  rejectButtonProps?: ButtonProps;
  confirmText?: string | JSX.Element;
  rejectText?: string | JSX.Element;
  primaryButtonOnRight?: boolean;
  fullWidthButton?: boolean;
  centered?: boolean;
  appearance?: DialogAppearance;
};

const ConfirmDialog = ({
  title,
  description,
  isVisible = true,
  onConfirm,
  onReject,
  confirmButtonProps,
  rejectButtonProps,
  confirmText = "Confirm",
  rejectText = "No",
  primaryButtonOnRight = false,
  fullWidthButton = false,
  centered = false,
  appearance,
}: ConfirmDialogProps) => {
  const { sm } = useBreakpoint();
  const isIceCream = appearance === "ice-cream";
  const buttonGroupStyle = isIceCream
    ? { marginTop: "24px" }
    : styles.buttonGroup;
  const buttonStyle = isIceCream ? { margin: 0 } : styles.button;
  let modalWidth = "auto";
  if (sm || isIceCream) {
    modalWidth = "303px";
  }

  const handleConfirmClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onConfirm?.();
  };

  const handleRejectClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onReject?.();
  };

  return (
    <Modal
      $appearance={appearance}
      destroyOnClose
      centered={centered}
      width={modalWidth}
      visible={isVisible}
      footer={null}
      closable={false}
      data-testid="confirm-dialog-modal"
      data-gtm-input-code="confirm-dialog-modal"
    >
      {isIceCream && (
        <Row justify="center">
          <IceCreamIcon
            src="/static/common/x-circle-filled.svg"
            alt="x-circle"
          />
        </Row>
      )}
      {isIceCream ? (
        <IceCreamTitle>{title}</IceCreamTitle>
      ) : (
        <Title>{title}</Title>
      )}
      {isIceCream ? (
        <IceCreamBody>{description}</IceCreamBody>
      ) : (
        <Body>{description}</Body>
      )}
      <Row justify="center" gutter={8} style={buttonGroupStyle}>
        <Col
          span={fullWidthButton ? 24 : 12}
          order={primaryButtonOnRight ? 1 : 0}
        >
          <IceCreamButton
            type="primary"
            data-testid="confirm-dialog-confirm-button"
            data-gtm-input-code="confirm-dialog-confirm-button"
            onClick={handleConfirmClick}
            style={buttonStyle}
            {...confirmButtonProps}
          >
            {confirmText}
          </IceCreamButton>
        </Col>
        <Col
          span={fullWidthButton ? 24 : 12}
          order={primaryButtonOnRight ? 0 : 1}
        >
          <IceCreamButton
            type="outlined"
            data-testid="confirm-dialog-reject-button"
            data-gtm-input-code="confirm-dialog-reject-button"
            onClick={handleRejectClick}
            style={buttonStyle}
            {...rejectButtonProps}
          >
            {rejectText}
          </IceCreamButton>
        </Col>
      </Row>
    </Modal>
  );
};
export default ConfirmDialog;
